import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { createTheme, Input, MenuItem, Select, ThemeProvider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import { useLocationState } from '../../../Common/Util/CustomHooks';
import * as request from '../../../Common/Util/Request';
import * as Button from '../../../Common/Component/Button';
import Table from '../../../Common/Component/Table';
import './index.scss';

const REASON_ELLIPSIS_LENGTH = 60;

/**
 * 수강 옵션 변경 이력 관리 — /lecture/option-change
 *
 * before/after 라벨과 처리자명은 이력 행에 저장된 스냅샷을 우선 표시한다.
 * (옵션이 이후 삭제·수정되어도 이력이 깨지지 않도록)
 */
function LectureOptionChangeList(props) {
  const [locationState, setLocationState] = useLocationState({
    startDate: undefined,
    endDate: undefined,
    searchOption: 'userName',
    searchText: '',
    filters: [],
  });
  const { startDate, endDate, filters } = locationState;
  const [searchOption, setSearchOption] = useState(locationState.searchOption);
  const [searchText, setSearchText] = useState(locationState.searchText);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);

  const onClickSearch = useCallback(() => {
    setLocationState({
      searchOption,
      searchText,
      filters: searchText ? [{ id: searchOption, value: searchText }] : [],
    });
  }, [setLocationState, searchOption, searchText]);

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      const params = {
        page: pageIndex + 1,
        limit: pageSize,
        filtered: JSON.stringify(filters),
      };
      if (startDate) {
        params.start_date = moment(startDate).format('YYYY-MM-DD');
      }
      if (endDate) {
        params.end_date = moment(endDate).format('YYYY-MM-DD');
      }
      request
        .optionChangeLogs(params)
        .then((result) => {
          const data = (result && result.data) || {};
          setLogs(data.rows || []);
          setTotal(data.count || 0);
        })
        .catch(() => {
          // 인터셉터가 detail 을 alert 으로 노출한다.
          setLogs([]);
          setTotal(0);
        });
    },
    [filters, startDate, endDate],
  );

  const columns = useMemo(
    () => [
      {
        Header: '변경일시',
        accessor: 'createdAt',
        // 여기서만 moment.utc() 를 쓰지 않는다. 되돌리지 말 것.
        //
        // 앱·DB 세션 timezone 이 '+00:00' 이고 컬럼이 datetime 이라 createdAt 은 UTC 로 저장되며
        // 조회 시 변환되지 않는다. moment.utc() 로 감싸면 UTC 가 그대로 찍혀 실제보다 9시간 이르게 보이고,
        // UTC 15시 이후 건은 날짜까지 하루 밀린다(예: 저장값 2026-09-03 16:07 → 실제 2026-09-04 01:07 KST).
        // moment(value) 는 브라우저 로컬(KST)로 변환하므로 정확하다.
        //
        // 어드민의 다른 화면들이 moment.utc() 를 쓰는 것은 전부 날짜만(YYYY-MM-DD) 출력하기 때문이다.
        // 날짜만 보면 9시간 오차가 대부분 드러나지 않을 뿐이며, 시각(HH:mm:ss)까지 보여주는 화면은 여기가 처음이다.
        //
        // 초까지 표시하는 이유: 감사 로그라 서버 로그와 대조해야 하고, 동시 변경 시 초 단위로만 구분된다
        // (실제로 연속 변경 이력이 같은 분(分) 안에서 1초 간격으로 쌓인 사례가 있다).
        Cell: ({ value }) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      { Header: '회원ID', accessor: 'FK_user.id' },
      {
        Header: '회원명',
        accessor: 'FK_user',
        Cell: ({ value: user }) => user?.name || user?.nickname || '-',
      },
      { Header: '강의ID', accessor: 'FK_class.id' },
      { Header: '강의명', accessor: 'FK_class.title' },
      {
        Header: '변경 전 → 변경 후',
        accessor: 'after_schedule_label',
        Cell: (data) => {
          const log = data.row.original;
          return (
            <div className="lecture-option-change__diff">
              <span className="lecture-option-change__diff-before">{log.before_schedule_label || '-'}</span>
              <span className="lecture-option-change__diff-arrow">↓</span>
              <span className="lecture-option-change__diff-after">{log.after_schedule_label || '-'}</span>
            </div>
          );
        },
      },
      {
        Header: '옵션가 변화',
        accessor: 'after_option_price',
        Cell: (data) => {
          const log = data.row.original;
          const before = log.before_option_price;
          const after = log.after_option_price;
          if (before === after || before === null || before === undefined || after === null || after === undefined) {
            return '-';
          }
          return `${Number(before).toLocaleString()} → ${Number(after).toLocaleString()}`;
        },
      },
      {
        Header: '처리자',
        accessor: 'admin_name',
        Cell: (data) => {
          const log = data.row.original;
          return log.admin_name || log.FK_admin_user?.name || '-';
        },
      },
      {
        Header: '사유',
        accessor: 'reason',
        Cell: ({ value }) => {
          if (!value) {
            return '-';
          }
          const truncated =
            value.length > REASON_ELLIPSIS_LENGTH ? `${value.slice(0, REASON_ELLIPSIS_LENGTH)}…` : value;
          return (
            <span className="lecture-option-change__reason" title={value}>
              {truncated}
            </span>
          );
        },
      },
    ],
    [],
  );

  const ejeTheme = createTheme({
    palette: {
      pink: {
        light: '#f47387',
        main: '#F24462',
        dark: '#a92f44',
        contrastText: '#fff',
      },
    },
  });

  return (
    <div className="lecture-option-change">
      <ThemeProvider theme={ejeTheme}>
        <div className="lecture-option-change__top">
          <div className="lecture-option-change__date">
            기간
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={startDate && dayjs(startDate)}
                maxDate={endDate && dayjs(endDate)}
                onChange={(value) => setLocationState({ startDate: dayjs(value).format('YYYY-MM-DD') })}
                label={startDate && 'From'}
                disableFuture
                views={['year', 'month', 'day']}
              />
              -
              <DatePicker
                value={endDate && dayjs(endDate)}
                minDate={startDate && dayjs(startDate)}
                onChange={(value) => setLocationState({ endDate: dayjs(value).format('YYYY-MM-DD') })}
                label={endDate && 'To'}
                disableFuture
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </div>
          <div className="lecture-option-change__search">
            <Select
              value={searchOption}
              onChange={(e) => setSearchOption(e.target.value)}
              displayEmpty
              color="pink"
              size="small"
            >
              <MenuItem value={'userName'} sx={{ fontSize: '0.875rem' }}>
                회원명
              </MenuItem>
              <MenuItem value={'classTitle'} sx={{ fontSize: '0.875rem' }}>
                강의명
              </MenuItem>
              <MenuItem value={'adminName'} sx={{ fontSize: '0.875rem' }}>
                처리자
              </MenuItem>
            </Select>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onClickSearch();
              }}
              color="pink"
              placeholder="검색어를 입력하세요."
            />
            <Button.MUI
              onClick={onClickSearch}
              variant="outlined"
              endIcon={<SearchIcon fontSize="small" />}
              color="pink"
              sx={{ height: '2rem' }}
            >
              검색
            </Button.MUI>
          </div>
        </div>
        <div className="lecture-option-change__total">총 {total}건</div>
        <Table columns={columns} data={logs} total={total} fetchData={fetchData} />
        {total === 0 && <div className="lecture-option-change__empty">조회된 옵션 변경 이력이 없습니다.</div>}
      </ThemeProvider>
    </div>
  );
}

export default LectureOptionChangeList;
